import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  Calendar,
  DollarSign,
  CheckCircle2,
  Clock,
  UploadCloud,
  FileText,
  AlertTriangle,
  RefreshCw,
  Search,
  Eye,
  Download,
  Paperclip,
  Check,
  X,
  UserX,
  CreditCard,
  TrendingUp,
  Receipt,
  Info,
  Lock,
  Star,
  ThumbsUp,
  ThumbsDown,
  MessageSquare,
  Award,
} from 'lucide-react';
import { repassesService } from '../services/repasses.service';
import type {
  RepasseExtratoClinicaDTO,
  RepasseDocumentoDTO,
  RepasseDocumentoTipoContract,
  RepasseItemApuracaoDTO,
} from '@sstexames/shared-types';

interface RepassesClinicaViewProps {
  clinicaId?: number;
  clinicaNome?: string;
}

export const RepassesClinicaView: React.FC<RepassesClinicaViewProps> = () => {
  const [competenciaSelecionada, setCompetenciaSelecionada] = useState<string>('');
  const [competenciasDisponiveis, setCompetenciasDisponiveis] = useState<
    Array<{ id: number; competencia: string; status: string }>
  >([]);
  const [extrato, setExtrato] = useState<RepasseExtratoClinicaDTO | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Filtros da tabela analítica
  const [buscaTabela, setBuscaTabela] = useState<string>('');
  const [filtroElegibilidade, setFiltroElegibilidade] = useState<string>('TODOS');

  // Modais de ação rápida
  const [modalNoShowItem, setModalNoShowItem] = useState<RepasseItemApuracaoDTO | null>(null);
  const [motivoNoShow, setMotivoNoShow] = useState<string>('Colaborador não compareceu no horário agendado');
  const [salvandoNoShow, setSalvandoNoShow] = useState<boolean>(false);

  const [modalAnexoItem, setModalAnexoItem] = useState<RepasseItemApuracaoDTO | null>(null);
  const [arquivoLaudo, setArquivoLaudo] = useState<File | null>(null);
  const [salvandoAnexo, setSalvandoAnexo] = useState<boolean>(false);

  // Estados de upload de faturamento
  const [uploadingTipo, setUploadingTipo] = useState<RepasseDocumentoTipoContract | null>(null);
  const [uploadFeedback, setUploadFeedback] = useState<{ tipo: 'success' | 'error'; msg: string } | null>(null);

  // Carregar extrato e lista de competências
  const carregarExtrato = useCallback(async (comp?: string) => {
    setLoading(true);
    setError(null);
    try {
      const data = await repassesService.obterMinhaApuracao(comp);
      setExtrato(data);
      if (data.competencia && !competenciaSelecionada) {
        setCompetenciaSelecionada(data.competencia);
      }
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : err instanceof Error
          ? err.message
          : 'Erro ao carregar dados de apuração do repasse.';
      setError(message || 'Erro ao carregar dados de apuração do repasse.');
    } finally {
      setLoading(false);
    }
  }, [competenciaSelecionada]);

  useEffect(() => {
    let ativo = true;

    const carregar = async () => {
      try {
        const lista = await repassesService.listarCompetencias();
        if (!ativo) return;
        setCompetenciasDisponiveis(lista);

        const compLiberadaCobranca =
          lista.find((c) =>
            ['ABERTA_COBRANCA', 'FECHAMENTO_BASE', 'EM_REVISAO'].includes(c.status),
          ) ||
          lista.find((c) =>
            ['DOCUMENTOS_RECEBIDOS', 'EM_AUDITORIA'].includes(c.status),
          ) ||
          lista[0];

        const compAlvo =
          competenciaSelecionada || (compLiberadaCobranca ? compLiberadaCobranca.competencia : undefined);
        if (compAlvo && !competenciaSelecionada) {
          setCompetenciaSelecionada(compAlvo);
        }

        const data = await repassesService.obterMinhaApuracao(compAlvo);
        if (!ativo) return;
        setExtrato(data);
      } catch (err: unknown) {
        if (!ativo) return;
        const message =
          err && typeof err === 'object' && 'response' in err
            ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
            : err instanceof Error
            ? err.message
            : 'Erro ao carregar dados de apuração do repasse.';
        setError(message || 'Erro ao carregar dados de apuração do repasse.');
      } finally {
        if (ativo) {
          setLoading(false);
        }
      }
    };

    void carregar();

    return () => {
      ativo = false;
    };
  }, [competenciaSelecionada]);

  // Formatação de valores monetários
  const formatMoney = (val?: number | null) => {
    return Number(val || 0).toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    });
  };

  // Upload de documento de faturamento
  const handleUploadDocumento = async (
    tipo: RepasseDocumentoTipoContract,
    file: File,
  ) => {
    if (!extrato) return;
    setUploadingTipo(tipo);
    setUploadFeedback(null);
    try {
      await repassesService.uploadDocumento(extrato.competencia_id, tipo, file);
      setUploadFeedback({
        tipo: 'success',
        msg: `Documento ${tipo} enviado com sucesso! Análise por IA iniciada.`,
      });
      await carregarExtrato(competenciaSelecionada);
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : `Falha ao enviar documento ${tipo}.`;
      setUploadFeedback({
        tipo: 'error',
        msg: msg || `Falha ao enviar documento ${tipo}.`,
      });
    } finally {
      setUploadingTipo(null);
    }
  };

  // Confirmar registro de No-Show
  const handleConfirmarNoShow = async () => {
    if (!modalNoShowItem) return;
    setSalvandoNoShow(true);
    try {
      await repassesService.registrarNoShow(modalNoShowItem.id, motivoNoShow);
      setModalNoShowItem(null);
      await carregarExtrato(competenciaSelecionada);
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : 'Erro ao registrar no-show.';
      alert(msg || 'Erro ao registrar no-show.');
    } finally {
      setSalvandoNoShow(false);
    }
  };

  // Confirmar envio de resultado avulso
  const handleConfirmarAnexoLaudo = async () => {
    if (!modalAnexoItem || !arquivoLaudo) return;
    setSalvandoAnexo(true);
    try {
      await repassesService.anexarResultado(modalAnexoItem.id, arquivoLaudo);
      setModalAnexoItem(null);
      setArquivoLaudo(null);
      await carregarExtrato(competenciaSelecionada);
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : 'Erro ao anexar resultado.';
      alert(msg || 'Erro ao anexar resultado.');
    } finally {
      setSalvandoAnexo(false);
    }
  };

  // Itens filtrados da tabela (sem No-Show ou Cancelados)
  const itensFiltrados = useMemo(() => {
    const lista = extrato?.itens || [];
    return lista.filter((item) => {
      const isNoShowOuCancelado =
        item.status_elegibilidade === 'NAO_FATURAVEL' ||
        item.status_agendamento === 'FALTOU_NO_SHOW' ||
        item.status_agendamento === 'NAO_COMPARECEU' ||
        item.status_agendamento === 'FALTOU' ||
        item.status_agendamento === 'CANCELADO' ||
        (item.motivo_recusa || '').toLowerCase().includes('no-show') ||
        (item.motivo_recusa || '').toLowerCase().includes('cancelado');

      if (isNoShowOuCancelado) {
        return false;
      }

      const matchBusca =
        !buscaTabela ||
        item.funcionario_nome.toLowerCase().includes(buscaTabela.toLowerCase()) ||
        item.funcionario_cpf.includes(buscaTabela) ||
        item.exame_nome.toLowerCase().includes(buscaTabela.toLowerCase()) ||
        item.empresa_nome.toLowerCase().includes(buscaTabela.toLowerCase());

      const isItemElegivel =
        item.status_elegibilidade === 'ELEGIVEL' ||
        item.status_elegibilidade === 'ELEGIVEL_AUTOMATICO' ||
        item.status_elegibilidade === 'APROVADO_EXCECAO';

      const matchElegibilidade =
        filtroElegibilidade === 'TODOS' ||
        (filtroElegibilidade === 'ELEGIVEL' && isItemElegivel) ||
        (filtroElegibilidade === 'PENDENTE' && !isItemElegivel);

      return matchBusca && matchElegibilidade;
    });
  }, [extrato?.itens, buscaTabela, filtroElegibilidade]);

  // Documentos organizados por tipo
  const documentoBoleto = extrato?.documentos.find((d) => d.tipo_documento === 'BOLETO');
  const documentoNfse = extrato?.documentos.find((d) => d.tipo_documento === 'NFSE');
  const documentoEspelho = extrato?.documentos.find((d) => d.tipo_documento === 'ESPELHO_CONCILIACAO');

  // Mapeamento de status da competência (7 estados do workflow de repasses)
  const statusBadgeMap: Record<string, { label: string; bg: string; text: string; desc: string }> = {
    ABERTA: {
      label: 'Previsão em Aberto (Informativo)',
      bg: 'bg-blue-100 text-blue-800 border-blue-200',
      text: 'text-blue-700',
      desc: 'Competência corrente em andamento. Valores e atendimentos provisionados em tempo real. O envio de cobrança será liberado após o fechamento mensal.',
    },
    FECHAMENTO_PENDENTE: {
      label: 'Virada de Mês em Processamento',
      bg: 'bg-amber-100 text-amber-800 border-amber-200',
      text: 'text-amber-700',
      desc: 'Virada de mês identificada. Aguardando consolidação do fechamento mensal.',
    },
    FECHAMENTO_PROCESSANDO: {
      label: 'Gerando Linha de Base V0',
      bg: 'bg-amber-100 text-amber-800 border-amber-200',
      text: 'text-amber-700',
      desc: 'Worker consolidando linha de base oficial (V0) com snapshot histórico imutável.',
    },
    ABERTA_COBRANCA: {
      label: 'Aberta para Cobrança (Faturamento Liberado)',
      bg: 'bg-emerald-100 text-emerald-800 border-emerald-200',
      text: 'text-emerald-700',
      desc: 'Linha de base V0 persistida! Envie sua Nota Fiscal de Serviços (NFS-e) e Boleto Bancário abaixo.',
    },
    EM_AUDITORIA: {
      label: 'Documentos em Auditoria',
      bg: 'bg-purple-100 text-purple-800 border-purple-200',
      text: 'text-purple-700',
      desc: 'Documentos recebidos em processamento e auditoria (conferência via IA e auditoria de repasses).',
    },
    EM_REVISAO: {
      label: 'Em Revisão de Laudos',
      bg: 'bg-indigo-100 text-indigo-800 border-indigo-200',
      text: 'text-indigo-700',
      desc: 'Alterações e regularização de laudos posteriores à V0 em análise.',
    },
    ENCERRADA: {
      label: 'Competência Encerrada',
      bg: 'bg-gray-100 text-gray-800 border-gray-200',
      text: 'text-gray-700',
      desc: 'Competência finalizada administrativamente com liquidação no Contas a Pagar.',
    },
    // Aliases para compatibilidade
    FECHAMENTO_BASE: {
      label: 'Aberta para Cobrança',
      bg: 'bg-emerald-100 text-emerald-800 border-emerald-200',
      text: 'text-emerald-700',
      desc: 'Faturamento liberado para envio de NFS-e e Boleto.',
    },
    DOCUMENTOS_RECEBIDOS: {
      label: 'Documentos em Auditoria',
      bg: 'bg-purple-100 text-purple-800 border-purple-200',
      text: 'text-purple-700',
      desc: 'Documentos recebidos em processamento.',
    },
    AUDITADO_PARCIAL: {
      label: 'Em Análise Financeira',
      bg: 'bg-indigo-100 text-indigo-800 border-indigo-200',
      text: 'text-indigo-700',
      desc: 'Auditoria em processamento pela nossa equipe de Contas a Pagar.',
    },
    PUBLICADO_CONTAS_PAGAR: {
      label: 'Aprovado & Programado para Pagamento',
      bg: 'bg-emerald-100 text-emerald-800 border-emerald-200',
      text: 'text-emerald-700',
      desc: 'Valores aprovados e lançados no Contas a Pagar para liquidação bancária.',
    },
    LIQUIDADO: {
      label: 'Pago / Liquidado',
      bg: 'bg-green-100 text-green-800 border-green-200',
      text: 'text-green-700',
      desc: 'Pagamento efetuado via Pix / TED com comprovante bancário emitido.',
    },
  };

  const statusAtual = extrato?.status || 'ABERTA';
  const faturamentoLiberado =
    statusAtual === 'ABERTA_COBRANCA' ||
    statusAtual === 'EM_REVISAO' ||
    statusAtual === 'FECHAMENTO_BASE' ||
    statusAtual === 'DOCUMENTOS_RECEBIDOS' ||
    statusAtual === 'EM_AUDITORIA';

  const statusInfo = statusBadgeMap[statusAtual] || {
    label: statusAtual,
    bg: 'bg-gray-100 text-gray-800 border-gray-200',
    text: 'text-gray-700',
    desc: 'Status da apuração.',
  };

  if (loading && !extrato) {
    return (
      <div className="flex flex-col items-center justify-center py-24 space-y-4">
        <RefreshCw className="w-10 h-10 text-emerald-600 animate-spin" />
        <p className="text-gray-600 font-medium text-lg">Carregando apuração financeira de repasses...</p>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fadeIn pb-12">
      {/* 1. Header com seletor de competência e status badge */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-emerald-50 text-emerald-600 rounded-xl">
              <DollarSign className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 tracking-tight">
                Apuração de Repasses e Faturamento
              </h1>
              <p className="text-sm text-gray-500">
                Acompanhe o provisionamento em tempo real, consulte exames realizados e envie notas fiscais e boletos.
              </p>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          {/* Seletor de Competência */}
          <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-xl px-3 py-2">
            <Calendar className="w-4 h-4 text-gray-500" />
            <select
              value={competenciaSelecionada}
              onChange={(e) => setCompetenciaSelecionada(e.target.value)}
              className="bg-transparent font-semibold text-gray-800 focus:outline-none cursor-pointer"
            >
              {competenciasDisponiveis.map((c) => {
                const statusResumo =
                  c.status === 'ABERTA_COBRANCA' ||
                  c.status === 'FECHAMENTO_BASE' ||
                  c.status === 'EM_REVISAO'
                    ? ' (Liberada p/ Cobrança)'
                    : c.status === 'ABERTA'
                    ? ' (Mês em Andamento)'
                    : c.status === 'EM_AUDITORIA' || c.status === 'DOCUMENTOS_RECEBIDOS'
                    ? ' (Em Auditoria)'
                    : c.status === 'LIQUIDADO' || c.status === 'ENCERRADA'
                    ? ' (Encerrada)'
                    : '';
                return (
                  <option key={c.id} value={c.competencia}>
                    Competência {c.competencia}
                    {statusResumo}
                  </option>
                );
              })}
            </select>
          </div>

          <button
            onClick={() => carregarExtrato(competenciaSelecionada)}
            disabled={loading}
            className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-xl transition"
            title="Recarregar"
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Banner de Erro Global se houver falha de requisição */}
      {error && (
        <div className="p-4 rounded-xl border border-red-200 bg-red-50 text-red-800 flex items-center justify-between">
          <div className="flex items-center gap-2 text-xs font-semibold">
            <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0" />
            <span>{error}</span>
          </div>
          <button
            onClick={() => setError(null)}
            className="text-red-500 hover:text-red-700 transition"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Banner explicativo de status da competência */}
      <div className={`p-4 rounded-xl border flex items-center justify-between gap-3 ${statusInfo.bg}`}>
        <div className="flex items-center gap-3">
          <Info className="w-5 h-5 flex-shrink-0" />
          <div>
            <span className="font-bold text-sm uppercase tracking-wider block">
              Status da Competência: {statusInfo.label}
            </span>
            <p className="text-xs opacity-90">{statusInfo.desc}</p>
          </div>
        </div>
      </div>

      {/* Banner de Cronograma de Pagamento Aprovado se publicado */}
      {extrato?.parcelas_aprovadas && extrato.parcelas_aprovadas.length > 0 && (
        <div className="bg-gradient-to-r from-emerald-900 to-slate-900 text-white p-5 rounded-2xl shadow-sm border border-emerald-700/50 space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-emerald-800/80 pb-3">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-500/20 text-emerald-400 rounded-xl">
                <CreditCard className="w-6 h-6" />
              </div>
              <div>
                <h3 className="text-base font-bold text-white flex items-center gap-2">
                  <span>Cronograma Aprovado de Pagamento</span>
                  <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                    {extrato.parcelas_aprovadas.length === 1
                      ? 'Pagamento Único à Vista'
                      : `Parcelado em ${extrato.parcelas_aprovadas.length}x`}
                  </span>
                </h3>
                <p className="text-xs text-emerald-200/80">
                  Valores e datas de liquidação autorizados pelo financeiro para esta competência.
                </p>
              </div>
            </div>
            <div className="text-right">
              <span className="text-[10px] uppercase font-bold text-emerald-300 block">Total Aprovado</span>
              <span className="text-xl font-black text-emerald-400">
                {formatMoney(
                  extrato.parcelas_aprovadas.reduce((acc, p) => acc + Number(p.valor || 0), 0),
                )}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {extrato.parcelas_aprovadas.map((parc, idx) => (
              <div
                key={idx}
                className="bg-slate-800/80 border border-slate-700/80 rounded-xl p-3 flex flex-col justify-between"
              >
                <div className="flex items-center justify-between text-xs mb-2">
                  <span className="font-bold text-slate-300">
                    Parcela {parc.numero_parcela} de {parc.total_parcelas || extrato.parcelas_aprovadas?.length}
                  </span>
                  <span
                    className={`text-[9px] px-2 py-0.5 rounded-full font-bold uppercase ${
                      parc.status === 'PAGO'
                        ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
                        : 'bg-amber-500/20 text-amber-300 border border-amber-500/30'
                    }`}
                  >
                    {parc.status === 'PAGO' ? 'Liquidado' : 'Programado'}
                  </span>
                </div>
                <div className="space-y-1">
                  <span className="text-lg font-black text-white block">
                    {formatMoney(parc.valor)}
                  </span>
                  <div className="flex items-center gap-1.5 text-xs text-emerald-300">
                    <Calendar className="w-3.5 h-3.5" />
                    <span>
                      Vencimento: <strong className="text-white">
                        {parc.data_vencimento
                          ? new Date(parc.data_vencimento).toLocaleDateString('pt-BR')
                          : '-'}
                      </strong>
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 2. 4 Cards de KPI Resumo */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {/* Card 1: Provisionado */}
        <div className="bg-white rounded-2xl p-5 border border-gray-200 shadow-sm relative overflow-hidden group hover:border-emerald-300 transition">
          <div className="flex items-center justify-between text-gray-500 mb-2">
            <span className="text-xs font-semibold uppercase tracking-wider">Total Provisionado</span>
            <span className="p-2 bg-blue-50 text-blue-600 rounded-xl">
              <Clock className="w-4 h-4" />
            </span>
          </div>
          <div className="text-2xl font-black text-gray-900">
            {formatMoney(extrato?.totais.total_provisionado)}
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Previsão bruta de todos os exames agendados e atendidos.
          </p>
        </div>

        {/* Card 2: Elegível */}
        <div className="bg-white rounded-2xl p-5 border border-gray-200 shadow-sm relative overflow-hidden group hover:border-emerald-300 transition">
          <div className="flex items-center justify-between text-gray-500 mb-2">
            <span className="text-xs font-semibold uppercase tracking-wider">Elegível para Repasse</span>
            <span className="p-2 bg-emerald-50 text-emerald-600 rounded-xl">
              <CheckCircle2 className="w-4 h-4" />
            </span>
          </div>
          <div className="text-2xl font-black text-emerald-600">
            {formatMoney(extrato?.totais.total_elegivel)}
          </div>
          <p className="text-xs text-emerald-700 mt-2 font-medium">
            Atendimentos concluídos com laudo/resultado anexado.
          </p>
        </div>

        {/* Card 3: Total Atendimentos */}
        <div className="bg-white rounded-2xl p-5 border border-gray-200 shadow-sm relative overflow-hidden group hover:border-emerald-300 transition">
          <div className="flex items-center justify-between text-gray-500 mb-2">
            <span className="text-xs font-semibold uppercase tracking-wider">Total Atendimentos</span>
            <span className="p-2 bg-purple-50 text-purple-600 rounded-xl">
              <TrendingUp className="w-4 h-4" />
            </span>
          </div>
          <div className="text-2xl font-black text-gray-900">
            {extrato?.totais.quantidade_atendimentos || 0}
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Exames ocupacionais realizados no período.
          </p>
        </div>

        {/* Card 4: Pendências de Laudo */}
        <div className="bg-white rounded-2xl p-5 border border-gray-200 shadow-sm relative overflow-hidden group hover:border-amber-300 transition">
          <div className="flex items-center justify-between text-gray-500 mb-2">
            <span className="text-xs font-semibold uppercase tracking-wider">Pendências de Resultado</span>
            <span className="p-2 bg-amber-50 text-amber-600 rounded-xl">
              <AlertTriangle className="w-4 h-4" />
            </span>
          </div>
          <div className="text-2xl font-black text-amber-600">
            {extrato?.totais.quantidade_pendencias_laudo || 0}
          </div>
          <p className="text-xs text-amber-700 mt-2">
            Anexe os resultados pendentes na tabela abaixo para destravar o pagamento.
          </p>
        </div>
      </div>

      {/* 3. Gráfico Interativo de Volume Diário (Realizados vs Pendentes) */}
      {extrato?.grafico_diario && extrato.grafico_diario.length > 0 && (
        <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <div>
              <h2 className="text-base font-bold text-gray-900">Evolução Diária de Atendimentos</h2>
              <p className="text-xs text-gray-500">
                Acompanhamento dia a dia dos exames realizados (elegíveis) e pendentes de resultado.
              </p>
            </div>
            <div className="flex items-center gap-4 text-xs font-medium">
              <span className="flex items-center gap-1.5 text-emerald-700">
                <span className="w-3 h-3 bg-emerald-500 rounded-sm inline-block" /> Realizados ({extrato.totais.quantidade_elegiveis})
              </span>
              <span className="flex items-center gap-1.5 text-amber-700">
                <span className="w-3 h-3 bg-amber-400 rounded-sm inline-block" /> Pendentes ({extrato.totais.quantidade_pendencias_laudo})
              </span>
            </div>
          </div>

          <div className="h-44 w-full flex items-end gap-1.5 pt-6 pb-2 overflow-x-auto">
            {extrato.grafico_diario.map((dia) => {
              const maxQtd = Math.max(
                ...extrato.grafico_diario.map((d) => d.total),
                1,
              );
              const heightTotal = Math.min(100, Math.round((dia.total / maxQtd) * 100));
              const heightRealizados = Math.round((dia.realizados / (dia.total || 1)) * 100);

              const diaNum = dia.data.split('-')[2] || dia.data;

              return (
                <div
                  key={dia.data}
                  className="flex-1 min-w-[28px] flex flex-col items-center gap-1 h-full justify-end group relative cursor-pointer"
                >
                  {/* Tooltip Hover */}
                  <div className="absolute -top-12 bg-gray-900 text-white text-[10px] px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition pointer-events-none whitespace-nowrap z-20 shadow-lg">
                    <p className="font-bold">{dia.data}</p>
                    <p>Total: {dia.total} ({formatMoney(dia.valor)})</p>
                    <p className="text-emerald-400">Realizados: {dia.realizados}</p>
                    <p className="text-amber-400">Pendentes: {dia.pendentes}</p>
                  </div>

                  <div
                    className="w-full bg-gray-100 rounded-t flex flex-col justify-end overflow-hidden"
                    style={{ height: `${heightTotal}%` }}
                  >
                    <div
                      className="w-full bg-amber-400"
                      style={{ height: `${100 - heightRealizados}%` }}
                    />
                    <div
                      className="w-full bg-emerald-500"
                      style={{ height: `${heightRealizados}%` }}
                    />
                  </div>
                  <span className="text-[10px] text-gray-400 font-mono">{diaNum}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* 3.1 NPS & Satisfação dos Pacientes */}
      {extrato?.nps && extrato.nps.total_respondidos > 0 && (
        <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-gray-100 pb-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-amber-50 text-amber-600 rounded-xl">
                <Award className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-base font-bold text-gray-900 flex items-center gap-2">
                  <span>Satisfação dos Colaboradores & Avaliação da Clínica</span>
                  <span
                    className={`text-xs px-2.5 py-0.5 rounded-full font-bold uppercase ${
                      extrato.nps.classificacao_nps === 'EXCELENTE'
                        ? 'bg-emerald-100 text-emerald-800'
                        : extrato.nps.classificacao_nps === 'MUITO_BOM'
                        ? 'bg-blue-100 text-blue-800'
                        : extrato.nps.classificacao_nps === 'RAZOAVEL'
                        ? 'bg-amber-100 text-amber-800'
                        : 'bg-red-100 text-red-800'
                    }`}
                  >
                    Zona de {extrato.nps.classificacao_nps.replace('_', ' ')}
                  </span>
                </h2>
                <p className="text-xs text-gray-500">
                  Índice NPS calculado com base em {extrato.nps.total_respondidos} feedbacks reais de colaboradores atendidos nesta clínica.
                </p>
              </div>
            </div>

            {/* Score NPS & Média Geral */}
            <div className="flex items-center gap-6">
              <div className="text-right">
                <span className="text-[10px] uppercase font-bold text-gray-400 block">Nota Geral</span>
                <div className="flex items-center gap-1">
                  <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
                  <span className="text-xl font-black text-gray-900">
                    {extrato.nps.media_geral.toFixed(1)}
                  </span>
                  <span className="text-xs text-gray-400 font-medium">/ 5.0</span>
                </div>
              </div>
              <div className="text-right pl-4 border-l border-gray-200">
                <span className="text-[10px] uppercase font-bold text-gray-400 block">Score NPS</span>
                <span
                  className={`text-2xl font-black ${
                    extrato.nps.score_nps >= 50
                      ? 'text-emerald-600'
                      : extrato.nps.score_nps >= 0
                      ? 'text-amber-600'
                      : 'text-red-600'
                  }`}
                >
                  {extrato.nps.score_nps > 0 ? `+${extrato.nps.score_nps}` : extrato.nps.score_nps}
                </span>
              </div>
            </div>
          </div>

          {/* Métricas do NPS: Promotores, Neutros, Detratores */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="p-3.5 bg-emerald-50/60 rounded-xl border border-emerald-100 flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div className="p-1.5 bg-emerald-100 text-emerald-700 rounded-lg">
                  <ThumbsUp className="w-4 h-4" />
                </div>
                <div>
                  <span className="text-xs font-bold text-emerald-900 block">Promotores</span>
                  <span className="text-[11px] text-emerald-700">Avaliações 5 estrelas</span>
                </div>
              </div>
              <span className="text-lg font-black text-emerald-800">{extrato.nps.total_promotores}</span>
            </div>

            <div className="p-3.5 bg-gray-50 rounded-xl border border-gray-200 flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div className="p-1.5 bg-gray-200 text-gray-700 rounded-lg">
                  <MessageSquare className="w-4 h-4" />
                </div>
                <div>
                  <span className="text-xs font-bold text-gray-900 block">Neutros</span>
                  <span className="text-[11px] text-gray-500">Avaliações 4 estrelas</span>
                </div>
              </div>
              <span className="text-lg font-black text-gray-800">{extrato.nps.total_neutros}</span>
            </div>

            <div className="p-3.5 bg-red-50/60 rounded-xl border border-red-100 flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div className="p-1.5 bg-red-100 text-red-700 rounded-lg">
                  <ThumbsDown className="w-4 h-4" />
                </div>
                <div>
                  <span className="text-xs font-bold text-red-900 block">Detratores</span>
                  <span className="text-[11px] text-red-700">Avaliações até 3 estrelas</span>
                </div>
              </div>
              <span className="text-lg font-black text-red-800">{extrato.nps.total_detratores}</span>
            </div>
          </div>

          {/* Opiniões em Destaque: Positivas e Oportunidades de Melhoria */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-1">
            {/* Elogios e Avaliações Positivas */}
            <div className="space-y-3">
              <h3 className="text-xs font-bold text-emerald-900 uppercase tracking-wider flex items-center gap-1.5">
                <ThumbsUp className="w-3.5 h-3.5 text-emerald-600" />
                Opiniões Positivas em Destaque
              </h3>
              {extrato.nps.comentarios_promotores && extrato.nps.comentarios_promotores.length > 0 ? (
                <div className="space-y-2.5">
                  {extrato.nps.comentarios_promotores.map((c, i) => (
                    <div key={i} className="bg-emerald-50/40 border border-emerald-100/80 rounded-xl p-3 text-xs space-y-1.5">
                      <div className="flex items-center justify-between">
                        <span className="font-bold text-gray-900">{c.colaborador_nome}</span>
                        <div className="flex items-center gap-0.5 text-amber-500">
                          <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                          <span className="font-bold text-[11px]">{c.nota.toFixed(1)}</span>
                        </div>
                      </div>
                      <p className="text-gray-700 italic">“{c.comentario}”</p>
                      {c.data && (
                        <span className="text-[10px] text-gray-400 block">
                          {new Date(c.data).toLocaleDateString('pt-BR')}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-4 bg-gray-50 border border-dashed border-gray-200 rounded-xl text-center text-xs text-gray-400">
                  Nenhum comentário por escrito registrado nesta faixa.
                </div>
              )}
            </div>

            {/* Oportunidades de Melhoria / Críticas Construtivas */}
            <div className="space-y-3">
              <h3 className="text-xs font-bold text-gray-800 uppercase tracking-wider flex items-center gap-1.5">
                <AlertTriangle className="w-3.5 h-3.5 text-amber-600" />
                Pontos de Atenção & Melhorias
              </h3>
              {extrato.nps.comentarios_detratores && extrato.nps.comentarios_detratores.length > 0 ? (
                <div className="space-y-2.5">
                  {extrato.nps.comentarios_detratores.map((c, i) => (
                    <div key={i} className="bg-amber-50/40 border border-amber-100/80 rounded-xl p-3 text-xs space-y-1.5">
                      <div className="flex items-center justify-between">
                        <span className="font-bold text-gray-900">{c.colaborador_nome}</span>
                        <div className="flex items-center gap-0.5 text-amber-700">
                          <Star className="w-3 h-3 text-amber-600" />
                          <span className="font-bold text-[11px]">{c.nota.toFixed(1)}</span>
                        </div>
                      </div>
                      <p className="text-gray-700 italic">“{c.comentario}”</p>
                      {c.data && (
                        <span className="text-[10px] text-gray-400 block">
                          {new Date(c.data).toLocaleDateString('pt-BR')}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-4 bg-emerald-50/50 border border-dashed border-emerald-200 rounded-xl text-center text-xs text-emerald-800">
                  ✨ Nenhum feedback negativo registrado para sua clínica! Parabéns pela excelência no atendimento.
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 4. Dropzones de Upload de Documentos de Faturamento (Boleto, NFS-e, Espelho) */}
      <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm space-y-6">
        <div>
          <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <Receipt className="w-5 h-5 text-emerald-600" />
            Envio de Documentos de Faturamento
          </h2>
          <p className="text-xs text-gray-500">
            Para o processamento e pagamento do repasse, é necessário enviar a Nota Fiscal de Serviços (NFS-e) e o Boleto Bancário da credenciada.
          </p>
        </div>

        {uploadFeedback && (
          <div
            className={`p-3.5 rounded-xl text-xs font-medium flex items-center justify-between ${
              uploadFeedback.tipo === 'success'
                ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
                : 'bg-red-50 text-red-800 border border-red-200'
            }`}
          >
            <span>{uploadFeedback.msg}</span>
            <button
              onClick={() => setUploadFeedback(null)}
              className="text-gray-400 hover:text-gray-700"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Banner Informativo quando o envio de cobrança estiver bloqueado */}
        {!faturamentoLiberado && (
          <div className="p-4 rounded-xl border border-amber-200 bg-amber-50 text-amber-900 flex items-start gap-3">
            <Lock className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="space-y-1">
              <span className="font-bold text-sm block">
                {statusAtual === 'ABERTA'
                  ? 'Faturamento Bloqueado — Competência em Andamento'
                  : statusAtual.startsWith('FECHAMENTO_')
                  ? 'Faturamento Temporariamente Bloqueado — Virada de Mês em Processamento'
                  : 'Faturamento Bloqueado — Competência Encerrada'}
              </span>
              <p className="text-xs text-amber-800 leading-relaxed">
                {statusAtual === 'ABERTA'
                  ? 'Os exames e valores exibidos acima são informativos e provisionados em tempo real. O envio da NFS-e e do Boleto será automaticamente liberado no dia 01 após a virada do mês, quando a linha de base oficial (V0) for consolidada.'
                  : statusAtual.startsWith('FECHAMENTO_')
                  ? 'A virada do mês foi identificada e nosso worker assíncrono está consolidando a linha de base imutável (V0). Assim que o processo for concluído (status ABERTA_COBRANCA), o envio dos documentos será liberado.'
                  : 'Esta competência já foi encerrada administrativamente e não aceita novos envios.'}
              </p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Dropzone 1: Boleto Bancário */}
          <DropzoneCard
            titulo="Boleto Bancário"
            descricao="PDF ou imagem legível do boleto para pagamento do repasse."
            tipo="BOLETO"
            documentoExistente={documentoBoleto}
            isUploading={uploadingTipo === 'BOLETO'}
            aceita=".pdf,.png,.jpg,.jpeg"
            onUpload={(file) => handleUploadDocumento('BOLETO', file)}
            disabled={!faturamentoLiberado}
            motivoBloqueio={
              statusAtual === 'ABERTA'
                ? 'Liberado no dia 01 após consolidação da V0'
                : 'Envio bloqueado para este status'
            }
          />

          {/* Dropzone 2: NFS-e */}
          <DropzoneCard
            titulo="Nota Fiscal (NFS-e)"
            descricao="PDF ou XML da Nota Fiscal de Serviços emitida contra a tomadora."
            tipo="NFSE"
            documentoExistente={documentoNfse}
            isUploading={uploadingTipo === 'NFSE'}
            aceita=".pdf,.xml"
            onUpload={(file) => handleUploadDocumento('NFSE', file)}
            disabled={!faturamentoLiberado}
            motivoBloqueio={
              statusAtual === 'ABERTA'
                ? 'Liberado no dia 01 após consolidação da V0'
                : 'Envio bloqueado para este status'
            }
          />

          {/* Dropzone 3: Espelho / Relatório Detalhado (Opcional) */}
          <DropzoneCard
            titulo="Espelho de Cobrança (Opcional)"
            descricao="Planilha ou relatório de conciliação detalhado da clínica."
            tipo="ESPELHO_CONCILIACAO"
            documentoExistente={documentoEspelho}
            isUploading={uploadingTipo === 'ESPELHO_CONCILIACAO'}
            aceita=".pdf,.xlsx,.csv"
            onUpload={(file) => handleUploadDocumento('ESPELHO_CONCILIACAO', file)}
            opcional
            disabled={!faturamentoLiberado}
            motivoBloqueio={
              statusAtual === 'ABERTA'
                ? 'Liberado no dia 01 após consolidação da V0'
                : 'Envio bloqueado para este status'
            }
          />
        </div>
      </div>

      {/* 5. Stepper da Esteira de Pagamento */}
      {extrato?.rastreamento_pagamento && (
        <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm space-y-4">
          <h2 className="text-base font-bold text-gray-900 flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-emerald-600" />
            Esteira de Pagamento e Liquidação
          </h2>

          <div className="flex flex-col md:flex-row items-center justify-between gap-4 pt-2">
            {extrato.rastreamento_pagamento.etapas.map((etapa, idx) => (
              <div key={idx} className="flex-1 flex items-center gap-3 w-full">
                <div
                  className={`w-9 h-9 rounded-full flex items-center justify-center font-bold text-xs flex-shrink-0 ${
                    etapa.concluida
                      ? 'bg-emerald-600 text-white'
                      : 'bg-gray-100 text-gray-400 border border-gray-200'
                  }`}
                >
                  {etapa.concluida ? <Check className="w-4 h-4" /> : idx + 1}
                </div>
                <div className="flex flex-col">
                  <span className={`text-xs font-bold ${etapa.concluida ? 'text-gray-900' : 'text-gray-400'}`}>
                    {etapa.nome}
                  </span>
                  <span className="text-[10px] text-gray-500">{etapa.descricao}</span>
                  {etapa.data && (
                    <span className="text-[10px] text-emerald-600 font-mono mt-0.5">
                      {new Date(etapa.data).toLocaleDateString('pt-BR')}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Download do Comprovante Pix se disponível */}
          {extrato.rastreamento_pagamento.comprovante_pix_url && (
            <div className="pt-4 border-t border-gray-100 flex items-center justify-between bg-emerald-50 p-4 rounded-xl">
              <div className="flex items-center gap-3 text-emerald-800">
                <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                <div>
                  <span className="font-bold text-sm block">Pagamento Efetuado via Pix</span>
                  <span className="text-xs text-emerald-700">
                    Transação:{' '}
                    <span className="font-mono">{extrato.rastreamento_pagamento.transacao_pix_id || 'Autenticada'}</span>
                  </span>
                </div>
              </div>
              <a
                href={extrato.rastreamento_pagamento.comprovante_pix_url}
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs rounded-xl shadow-sm flex items-center gap-2 transition"
              >
                <Download className="w-4 h-4" /> Baixar Comprovante Pix
              </a>
            </div>
          )}
        </div>
      )}

      {/* 6. Tabela Analítica dos Atendimentos com Ações Rápidas */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden space-y-4 p-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h2 className="text-base font-bold text-gray-900">Detalhamento dos Atendimentos</h2>
            <p className="text-xs text-gray-500">
              Consulte a lista analítica de atendimentos, anexe resultados faltantes ou registre não comparecimento.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            {/* Campo de Busca */}
            <div className="relative">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-2.5" />
              <input
                type="text"
                placeholder="Buscar colaborador, CPF, exame..."
                value={buscaTabela}
                onChange={(e) => setBuscaTabela(e.target.value)}
                className="pl-9 pr-3 py-1.5 text-xs bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:border-emerald-500 w-64"
              />
            </div>

            {/* Filtro de Elegibilidade */}
            <select
              value={filtroElegibilidade}
              onChange={(e) => setFiltroElegibilidade(e.target.value)}
              className="text-xs bg-gray-50 border border-gray-200 rounded-xl px-3 py-1.5 font-medium text-gray-700 focus:outline-none cursor-pointer"
            >
              <option value="TODOS">Todos os Atendimentos</option>
              <option value="ELEGIVEL">Apenas Elegíveis</option>
              <option value="PENDENTE">Com Pendência de Resultado</option>
            </select>
          </div>
        </div>

        {/* Tabela */}
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs text-gray-600 border-collapse">
            <thead>
              <tr className="bg-gray-50/75 border-y border-gray-200 font-bold text-gray-700 uppercase tracking-wider text-[10px]">
                <th className="py-3 px-4">Data</th>
                <th className="py-3 px-4">Colaborador / CPF</th>
                <th className="py-3 px-4">Empresa</th>
                <th className="py-3 px-4">Procedimento / Exame</th>
                <th className="py-3 px-4">Status Resultado</th>
                <th className="py-3 px-4 text-center">Elegibilidade</th>
                <th className="py-3 px-4 text-right">Ações Rápidas</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {itensFiltrados.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-8 text-center text-gray-400">
                    Nenhum atendimento encontrado para os filtros selecionados.
                  </td>
                </tr>
              ) : (
                  itensFiltrados.map((item) => {
                    const isEmAuditoria =
                      item.status_agendamento === 'LAUDO_EM_AUDITORIA' ||
                      (item.motivo_recusa || '').toLowerCase().includes('auditoria') ||
                      (item.motivo_recusa || '').toLowerCase().includes('quarentena');

                    return (
                      <tr key={item.id} className="hover:bg-gray-50/50 transition">
                        <td className="py-3 px-4 whitespace-nowrap font-medium text-gray-900">
                          {item.data_atendimento
                            ? new Date(item.data_atendimento).toLocaleDateString('pt-BR')
                            : 'Não informada'}
                        </td>
                        <td className="py-3 px-4">
                          <span className="font-bold text-gray-900 block">{item.funcionario_nome}</span>
                          <span className="text-[10px] text-gray-400 font-mono">{item.funcionario_cpf}</span>
                        </td>
                        <td className="py-3 px-4 truncate max-w-[180px]" title={item.empresa_nome}>
                          {item.empresa_nome}
                        </td>
                        <td className="py-3 px-4 font-semibold text-gray-800">
                          {item.exame_nome}
                        </td>
                        <td className="py-3 px-4">
                          {isEmAuditoria ? (
                            <span className="inline-flex items-center gap-1 text-purple-700 bg-purple-50 px-2 py-0.5 rounded text-[10px] font-bold">
                              <Clock className="w-3 h-3" /> Em Validação (Auditoria)
                            </span>
                          ) : item.resultado_anexado ? (
                            <span className="inline-flex items-center gap-1 text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded text-[10px] font-bold">
                              <Check className="w-3 h-3" /> Resultado Anexado
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-amber-700 bg-amber-50 px-2 py-0.5 rounded text-[10px] font-bold">
                              <Clock className="w-3 h-3" /> Pendente de Laudo
                            </span>
                          )}
                        </td>
                        <td className="py-3 px-4 text-center">
                          {item.status_elegibilidade === 'ELEGIVEL' ||
                          item.status_elegibilidade === 'ELEGIVEL_AUTOMATICO' ||
                          item.status_elegibilidade === 'APROVADO_EXCECAO' ? (
                            <span className="inline-flex items-center gap-1 text-emerald-800 bg-emerald-100 px-2.5 py-0.5 rounded-full text-[10px] font-bold">
                              <CheckCircle2 className="w-3 h-3 text-emerald-600" /> Pré-Aprovado
                            </span>
                          ) : isEmAuditoria ? (
                            <span
                              className="inline-flex items-center gap-1 text-purple-800 bg-purple-100 px-2.5 py-0.5 rounded-full text-[10px] font-bold cursor-help"
                              title={item.motivo_recusa || 'Laudo anexado em quarentena para validação por IA e auditoria'}
                            >
                              <Clock className="w-3 h-3 text-purple-600" /> Em Análise
                            </span>
                          ) : (
                            <span
                              className="inline-flex items-center gap-1 text-amber-800 bg-amber-100 px-2.5 py-0.5 rounded-full text-[10px] font-bold cursor-help"
                              title={item.motivo_recusa || 'Aguardando resultado para liberação de repasse'}
                            >
                              <Clock className="w-3 h-3 text-amber-600" /> Pendente
                            </span>
                          )}
                        </td>
                        <td className="py-3 px-4 text-right whitespace-nowrap space-x-2">
                          {/* Botão de Anexar Resultado se não estiver anexado nem em auditoria */}
                          {!item.resultado_anexado && !isEmAuditoria && (
                            <button
                              onClick={() => setModalAnexoItem(item)}
                              className="px-2.5 py-1 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 font-bold rounded-lg text-[10px] inline-flex items-center gap-1 transition"
                            >
                              <Paperclip className="w-3 h-3" /> Anexar Laudo
                            </button>
                          )}

                          {/* Botão de No-Show */}
                          {!item.resultado_anexado && !isEmAuditoria && (
                            <button
                              onClick={() => setModalNoShowItem(item)}
                              className="px-2.5 py-1 bg-gray-50 hover:bg-gray-100 text-gray-600 font-bold rounded-lg text-[10px] inline-flex items-center gap-1 transition"
                            >
                              <UserX className="w-3 h-3" /> No-Show
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal 1: Registro de No-Show */}
      {modalNoShowItem && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full space-y-4 shadow-xl border border-gray-100 animate-scaleUp">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">
                <UserX className="w-5 h-5 text-amber-600" />
                Registrar Não Comparecimento (No-Show)
              </h3>
              <button
                onClick={() => setModalNoShowItem(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="text-xs text-gray-600">
              Confirme o não comparecimento do colaborador{' '}
              <strong className="text-gray-900">{modalNoShowItem.funcionario_nome}</strong> no exame{' '}
              <strong className="text-gray-900">{modalNoShowItem.exame_nome}</strong>. O item será atualizado na apuração.
            </p>

            <div>
              <label className="text-xs font-bold text-gray-700 block mb-1">
                Motivo do Não Comparecimento
              </label>
              <textarea
                value={motivoNoShow}
                onChange={(e) => setMotivoNoShow(e.target.value)}
                className="w-full text-xs p-2.5 border border-gray-200 rounded-xl focus:outline-none focus:border-emerald-500"
                rows={3}
              />
            </div>

            <div className="flex items-center justify-end gap-2 pt-2">
              <button
                onClick={() => setModalNoShowItem(null)}
                className="px-4 py-2 text-xs font-bold text-gray-600 hover:bg-gray-100 rounded-xl transition"
              >
                Cancelar
              </button>
              <button
                onClick={handleConfirmarNoShow}
                disabled={salvandoNoShow}
                className="px-4 py-2 text-xs font-bold bg-amber-600 hover:bg-amber-700 text-white rounded-xl shadow-sm transition flex items-center gap-1.5"
              >
                {salvandoNoShow && <RefreshCw className="w-3.5 h-3.5 animate-spin" />}
                Confirmar No-Show
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal 2: Anexar Resultado Pendente */}
      {modalAnexoItem && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full space-y-4 shadow-xl border border-gray-100 animate-scaleUp">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">
                <Paperclip className="w-5 h-5 text-emerald-600" />
                Anexar Laudo / Resultado Pendente
              </h3>
              <button
                onClick={() => {
                  setModalAnexoItem(null);
                  setArquivoLaudo(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="text-xs text-gray-600">
              Anexe o arquivo de laudo ou resultado assinado do colaborador{' '}
              <strong className="text-gray-900">{modalAnexoItem.funcionario_nome}</strong>. O documento será encaminhado para validação automática por IA e auditoria antes da liberação do repasse financeiro.
            </p>

            <div className="border-2 border-dashed border-gray-200 rounded-xl p-4 text-center hover:border-emerald-400 transition cursor-pointer">
              <input
                type="file"
                accept=".pdf,.png,.jpg,.jpeg"
                onChange={(e) => {
                  if (e.target.files && e.target.files[0]) {
                    setArquivoLaudo(e.target.files[0]);
                  }
                }}
                className="hidden"
                id="modal-laudo-upload"
              />
              <label htmlFor="modal-laudo-upload" className="cursor-pointer">
                <UploadCloud className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                <span className="text-xs font-bold text-emerald-600 block">
                  {arquivoLaudo ? arquivoLaudo.name : 'Selecionar arquivo de resultado (PDF ou Imagem)'}
                </span>
                <span className="text-[10px] text-gray-400">Tamanho máximo: 15MB</span>
              </label>
            </div>

            <div className="flex items-center justify-end gap-2 pt-2">
              <button
                onClick={() => {
                  setModalAnexoItem(null);
                  setArquivoLaudo(null);
                }}
                className="px-4 py-2 text-xs font-bold text-gray-600 hover:bg-gray-100 rounded-xl transition"
              >
                Cancelar
              </button>
              <button
                onClick={handleConfirmarAnexoLaudo}
                disabled={!arquivoLaudo || salvandoAnexo}
                className="px-4 py-2 text-xs font-bold bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded-xl shadow-sm transition flex items-center gap-1.5"
              >
                {salvandoAnexo && <RefreshCw className="w-3.5 h-3.5 animate-spin" />}
                Salvar Resultado
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

interface DropzoneCardProps {
  titulo: string;
  descricao: string;
  tipo: RepasseDocumentoTipoContract;
  documentoExistente?: RepasseDocumentoDTO;
  isUploading: boolean;
  aceita: string;
  onUpload: (file: File) => void;
  opcional?: boolean;
  disabled?: boolean;
  motivoBloqueio?: string;
}

const DropzoneCard: React.FC<DropzoneCardProps> = ({
  titulo,
  descricao,
  tipo,
  documentoExistente,
  isUploading,
  aceita,
  onUpload,
  opcional,
  disabled,
  motivoBloqueio,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      onUpload(e.target.files[0]);
    }
  };

  return (
    <div
      data-tipo-documento={tipo}
      className={`border rounded-2xl p-5 flex flex-col justify-between space-y-4 transition shadow-sm ${
        disabled && !documentoExistente
          ? 'bg-gray-50/70 border-gray-200 opacity-90'
          : 'bg-gray-50/50 hover:bg-white hover:border-emerald-300 border-gray-200'
      }`}
    >
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2">
            <FileText
              className={`w-4 h-4 ${
                disabled && !documentoExistente ? 'text-gray-400' : 'text-emerald-600'
              }`}
            />
            {titulo}
          </h3>
          {opcional && (
            <span className="text-[10px] text-gray-400 bg-gray-100 px-2 py-0.5 rounded font-medium">
              Opcional
            </span>
          )}
        </div>
        <p className="text-xs text-gray-500 leading-relaxed">{descricao}</p>
      </div>

      {/* Documento Já Enviado */}
      {documentoExistente ? (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 space-y-2">
          <div className="flex items-center justify-between text-xs">
            <span className="font-bold text-emerald-900 truncate max-w-[180px]">
              {documentoExistente.nome_arquivo}
            </span>
            <span className="text-[10px] font-bold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full flex items-center gap-1">
              <Check className="w-3 h-3" /> Recebido
            </span>
          </div>

          <div className="text-[10px] text-emerald-700 space-y-0.5 font-mono">
            <p>Hash: {documentoExistente.sha256.substring(0, 16)}...</p>
            <p>Enviado em: {new Date(documentoExistente.criado_em).toLocaleDateString('pt-BR')}</p>
          </div>

          {documentoExistente.url_download && (
            <a
              href={documentoExistente.url_download}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[10px] font-bold text-emerald-700 hover:text-emerald-900 inline-flex items-center gap-1 pt-1"
            >
              <Eye className="w-3 h-3" /> Visualizar Arquivo
            </a>
          )}
        </div>
      ) : disabled ? (
        /* Bloqueado */
        <div className="border-2 border-dashed border-gray-200 rounded-xl p-5 text-center bg-gray-100/50 cursor-not-allowed">
          <div className="space-y-1">
            <Lock className="w-6 h-6 text-gray-400 mx-auto mb-1" />
            <span className="text-xs font-bold text-gray-500 block">Envio Bloqueado</span>
            <span className="text-[10px] text-gray-400 block">
              {motivoBloqueio || 'Liberado após o fechamento da competência (V0)'}
            </span>
          </div>
        </div>
      ) : (
        /* Dropzone para upload */
        <div
          onClick={() => fileInputRef.current?.click()}
          className="border-2 border-dashed border-gray-200 hover:border-emerald-500 rounded-xl p-5 text-center cursor-pointer transition bg-white"
        >
          <input
            ref={fileInputRef}
            type="file"
            accept={aceita}
            onChange={handleFileChange}
            className="hidden"
          />
          {isUploading ? (
            <div className="flex flex-col items-center gap-2 py-2">
              <RefreshCw className="w-6 h-6 text-emerald-600 animate-spin" />
              <span className="text-xs font-bold text-emerald-600">Enviando e processando...</span>
            </div>
          ) : (
            <div className="space-y-1">
              <UploadCloud className="w-7 h-7 text-gray-400 mx-auto mb-1 group-hover:text-emerald-500" />
              <span className="text-xs font-bold text-emerald-600 block">Clique para fazer upload</span>
              <span className="text-[10px] text-gray-400 block">Formatos: {aceita}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default RepassesClinicaView;
